import ApplicationController from './application_controller';
import initializeDataTable from '../lib/datatables/initialize';

export default class extends ApplicationController {
  static targets = [
    // Billing
    'agingReportDataTable',
    'authorizationsDataTable',
    // Clinical documentation
    'visitNoteBreakdownDataTable',
    'evaluationBreakdownDataTable',
    'dischargesDataTable',
    'eventsMissingDocumentationDataTable',
    'unsignedClinicalDocumentationDataTable',
    // Attendance
    'clinicCanceledDataTable',
    'clientCanceledDataTable',
    'noShowDataTable',
    'missedSessionLogsDataTable',
    'presentDataTable',
  ];

  static values = { dataTablesLoaded: Boolean };

  initialize() {
    this.dataTablesLoaded = false;
  }

  initDataTables() {
    if (!this.dataTablesLoaded) {
      this.setDataTables();
      this.dataTablesLoaded = true;
    }
  }

  authorizationsDataTableTargetConnected() {
    this.initDataTables();
    if (window.$.fn.DataTable.isDataTable('#authorizations-table')) {
      window.$('#authorizations-table').DataTable().destroy();
    }
    window.$('#authorizations-table').DataTable({
      order: [[7, 'asc']],
    });
  }

  agingReportDataTableTargetConnected() {
    this.initDataTables();
    if (window.$.fn.DataTable.isDataTable('#aging-table')) {
      window.$('#aging-table').DataTable().destroy();
    }
    window.$('#aging-table').DataTable({
      order: [[1, 'desc']],
    });
  }

  visitNoteBreakdownDataTableTargetConnected() {
    this.initDataTables();
    if (window.$.fn.DataTable.isDataTable('#visit-note-table')) {
      window.$('#visit-note-table').DataTable().destroy();
    }
    window.$('#visit-note-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  evaluationBreakdownDataTableTargetConnected() {
    this.initDataTables();
    if (window.$.fn.DataTable.isDataTable('#evaluation-table')) {
      window.$('#evaluation-table').DataTable().destroy();
    }
    window.$('#evaluation-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  dischargesDataTableTargetConnected() {
    this.initDataTables();
    if (window.$.fn.DataTable.isDataTable('#discharge-table')) {
      window.$('#discharge-table').DataTable().destroy();
    }
    window.$('#discharge-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  eventsMissingDocumentationDataTableTargetConnected() {
    this.initDataTables();
    if (window.$.fn.DataTable.isDataTable('#missing-visit-notes-table')) {
      window.$('#missing-visit-notes-table').DataTable().destroy();
    }
    window.$('#missing-visit-notes-table').DataTable({
      order: [[0, 'asc']],
    });
  }

  unsignedClinicalDocumentationDataTableTargetConnected() {
    initializeDataTable();
  }

  clinicCanceledDataTableTargetConnected(element) {
    this.initDataTables();
    this.attendancePageDataTableInit(element);
  }

  clientCanceledDataTableTargetConnected(element) {
    this.initDataTables();
    this.attendancePageDataTableInit(element);
  }

  noShowDataTableTargetConnected(element) {
    this.initDataTables();
    this.attendancePageDataTableInit(element);
  }

  missedSessionLogsDataTableTargetConnected(element) {
    this.initDataTables();
    const sessionLogs = window.$('.missed-session-logs-table', element)[0];
    if (sessionLogs && window.$.fn.DataTable.isDataTable(sessionLogs)) {
      window.$(sessionLogs).DataTable().destroy();
    }
    window.$(sessionLogs).DataTable({
      order: [[4, 'desc']],
    });
  }

  presentDataTableTargetConnected(element) {
    this.initDataTables();
    const patientsAttendanceRates = window.$('#patients-attendance-rates', element)[0];
    if (patientsAttendanceRates && window.$.fn.DataTable.isDataTable(patientsAttendanceRates)) {
      window.$(patientsAttendanceRates).DataTable().destroy();
    }
    window.$(patientsAttendanceRates).DataTable({
      order: [[5, 'desc']],
    });
    const therapistAttendanceRates = window.$('#therapists-attendance-rates', element)[0];
    if (therapistAttendanceRates && window.$.fn.DataTable.isDataTable(therapistAttendanceRates)) {
      window.$(therapistAttendanceRates).DataTable().destroy();
    }
    window.$(therapistAttendanceRates).DataTable({
      order: [[5, 'desc']],
    });
  }

  attendancePageDataTableInit(element) {
    const patients = window.$('.patients-data-table', element)[0];
    if (patients && window.$.fn.DataTable.isDataTable(patients)) {
      window.$(patients).DataTable().destroy();
    }
    window.$(patients).DataTable({
      order: [[2, 'desc']],
    });

    const therapists = window.$('.therapists-data-table', element)[0];
    if (therapists && window.$.fn.DataTable.isDataTable(therapists)) {
      window.$(therapists).DataTable().destroy();
    }
    window.$(therapists).DataTable({
      order: [[2, 'desc']],
    });

    const hours = window.$('.hours-data-table', element)[0];
    if (hours && window.$.fn.DataTable.isDataTable(hours)) {
      window.$(hours).DataTable().destroy();
    }
    window.$(hours).DataTable({
      order: [[1, 'desc']],
    });

    const days = window.$('.days-data-table', element)[0];
    if (days && window.$.fn.DataTable.isDataTable(days)) {
      window.$(days).DataTable().destroy();
    }
    window.$(days).DataTable({
      order: [[1, 'desc']],
    });
  }

  setDataTables() {
    // Method relies on $ and moment defined on window which comes from the caseloads pack
    function strip(d) {
      if (typeof d === 'string') {
        // Strip HTML tags and newline characters if possible
        d = d.replace(/(<.*?>)|(\r?\n|\r)/g, ''); // Strip out surrounding white space

        d = d.trim();
      }

      return d;
    }

    window.$.fn.dataTable.moment = function (format, locale, reverseEmpties) {
      const types = window.$.fn.dataTable.ext.type; // Add type detection

      types.detect.unshift((d) => {
        d = strip(d); // Null and empty values are acceptable

        if (d === '' || d === null) {
          return 'moment-' + format;
        }

        return moment(d, format, locale, true).isValid() ? 'moment-' + format : null;
      }); // Add sorting method - use an integer for the sorting

      types.order['moment-' + format + '-pre'] = function (d) {
        d = strip(d);
        return !moment(d, format, locale, true).isValid() ? reverseEmpties ? -Infinity : Infinity : parseInt(moment(d, format, locale, true).format('x'), 10);
      };
    };
  }
}
